/**
 * SignUpPage Component
 *
 * Public onboarding interest form for new customers arriving from the
 * Hire It Now landing pages. On submission it calls POST /v1/public/onboarding-enquiry
 * which emails the onboarding team and confirms receipt to the user.
 *
 * No Firebase authentication is required.
 */

import { useState, useCallback, type SubmitEvent } from 'react';
import { Link } from 'react-router-dom';
import { Turnstile } from '@marsidev/react-turnstile';

import heroBg1 from '../../../common/assets/images/hero-construction-1.jpg';
import heroBg4 from '../../../common/assets/images/hero-construction-4.jpg';

function getBackgroundImage(): string {
  const hour = new Date().getHours();
  return `url('${hour >= 9 && hour < 16 ? heroBg1 : heroBg4}')`;
}

type InterestType = 'hirer' | 'partner' | 'supplier';

interface FormState {
  fullName: string;
  email: string;
  companyName: string;
  interest: InterestType | '';
  phone: string;
  message: string;
}

const INITIAL_FORM: FormState = {
  fullName: '',
  email: '',
  companyName: '',
  interest: '',
  phone: '',
  message: '',
};

const INTEREST_OPTIONS: { value: InterestType; label: string; description: string }[] = [
  {
    value: 'hirer',
    label: 'Hirer',
    description: 'I need to hire equipment for projects or operations',
  },
  {
    value: 'partner',
    label: 'Partner / Broker',
    description: 'I manage hire on behalf of clients or operate as a broker',
  },
  {
    value: 'supplier',
    label: 'Supplier',
    description: 'I supply or rent out equipment to hirers',
  },
];

const API_BASE_URL = import.meta.env.VITE_API_URL ?? '';
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY ?? '';

export function SignUpPage() {
  const backgroundImage = getBackgroundImage();

  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setField = useCallback(
    <K extends keyof FormState>(key: K, value: FormState[K]) => {
      setForm((prev) => ({ ...prev, [key]: value }));
      setError(null);
    },
    [],
  );

  const handleSubmit = useCallback(
    async (e: SubmitEvent<HTMLFormElement>) => {
      e.preventDefault();

      if (!form.interest) {
        setError('Please select the option that best describes your interest.');
        return;
      }

      if (!TURNSTILE_SITE_KEY) {
        // Dev mode: allow submission without a real token
      } else if (!turnstileToken) {
        setError('Please complete the CAPTCHA check before submitting.');
        return;
      }

      setIsSubmitting(true);
      setError(null);

      try {
        const response = await fetch(`${API_BASE_URL}/v1/public/onboarding-enquiry`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            full_name: form.fullName,
            email: form.email,
            company_name: form.companyName,
            interest: form.interest,
            phone: form.phone || null,
            message: form.message || null,
            turnstile_token: turnstileToken ?? 'dev-bypass',
          }),
        });

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(
            (data as { detail?: string }).detail ??
              'Something went wrong. Please try again.',
          );
        }

        setSubmitted(true);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    },
    [form, turnstileToken],
  );

  // Success screen
  if (submitted) {
    return (
      <div className="login-page" style={{ backgroundImage }}>
        <div className="login-container signup-success-container">
          <div className="signup-success">
            <div className="signup-success-icon" aria-hidden="true">✓</div>
            <h1 className="login-title">You're on the list!</h1>
            <p className="login-subtitle">
              Thanks for your interest in Hire It Now. Our onboarding team will review your details
              and reach out to you shortly.
            </p>
            <p className="signup-success-hint">
              In the meantime, if you have an existing account you can{' '}
              <Link to="/" className="link-button">
                sign in here
              </Link>
              .
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page" style={{ backgroundImage }}>
      <div className="login-container signup-container">
        {/* Header */}
        <div className="login-header">
          <div className="logo">
            <svg
              viewBox="0 0 40 40"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="logo-icon"
              aria-hidden="true"
            >
              <rect width="40" height="40" rx="8" fill="#1e4d3e" />
              <path
                d="M12 14h6v12h-6zM22 14h6v12h-6zM12 20h16"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            <span className="logo-text">
              Hire <span className="logo-text-accent" style={{ color: '#1e4d3e' }}>It</span> Now
            </span>
          </div>
          <h1 className="login-title">Get started</h1>
          <p className="login-subtitle">
            Tell us about yourself and our team will set you up.
          </p>
        </div>

        {/* Error display */}
        {error && (
          <div className="login-error" role="alert">
            <span className="error-icon" aria-hidden="true">!</span>
            <div className="error-content">
              <p className="error-message">{error}</p>
              <button
                type="button"
                className="error-dismiss"
                onClick={() => setError(null)}
                aria-label="Dismiss error"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Form */}
        <form className="email-login-form" onSubmit={handleSubmit} noValidate>
          <div className="form-group">
            <label htmlFor="fullName" className="form-label">
              Full name <span aria-hidden="true">*</span>
            </label>
            <input
              type="text"
              id="fullName"
              className="form-input"
              value={form.fullName}
              onChange={(e) => setField('fullName', e.target.value)}
              placeholder="Jane Smith"
              required
              disabled={isSubmitting}
              autoComplete="name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="email" className="form-label">
              Work email <span aria-hidden="true">*</span>
            </label>
            <input
              type="email"
              id="email"
              className="form-input"
              value={form.email}
              onChange={(e) => setField('email', e.target.value)}
              placeholder="jane@company.com"
              required
              disabled={isSubmitting}
              autoComplete="email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="companyName" className="form-label">
              Company / Organisation <span aria-hidden="true">*</span>
            </label>
            <input
              type="text"
              id="companyName"
              className="form-input"
              value={form.companyName}
              onChange={(e) => setField('companyName', e.target.value)}
              placeholder="Acme Construction Ltd"
              required
              disabled={isSubmitting}
              autoComplete="organization"
            />
          </div>

          {/* Interest type */}
          <fieldset className="signup-interest-fieldset" disabled={isSubmitting}>
            <legend className="form-label">
              I am a… <span aria-hidden="true">*</span>
            </legend>
            <div className="signup-interest-options">
              {INTEREST_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  className={`signup-interest-card${form.interest === opt.value ? ' signup-interest-card--selected' : ''}`}
                >
                  <input
                    type="radio"
                    name="interest"
                    value={opt.value}
                    checked={form.interest === opt.value}
                    onChange={() => setField('interest', opt.value)}
                    className="signup-interest-radio"
                  />
                  <span className="signup-interest-label">{opt.label}</span>
                  <span className="signup-interest-desc">{opt.description}</span>
                </label>
              ))}
            </div>
          </fieldset>

          <div className="form-group">
            <label htmlFor="phone" className="form-label">
              Phone <span className="form-label-optional">(optional)</span>
            </label>
            <input
              type="tel"
              id="phone"
              className="form-input"
              value={form.phone}
              onChange={(e) => setField('phone', e.target.value)}
              placeholder="+44 7700 900000"
              disabled={isSubmitting}
              autoComplete="tel"
            />
          </div>

          <div className="form-group">
            <label htmlFor="message" className="form-label">
              Anything else we should know? <span className="form-label-optional">(optional)</span>
            </label>
            <textarea
              id="message"
              className="form-input form-textarea"
              value={form.message}
              onChange={(e) => setField('message', e.target.value)}
              placeholder="Tell us about your use case, team size, or any questions you have…"
              rows={3}
              disabled={isSubmitting}
            />
          </div>

          {/* Turnstile CAPTCHA */}
          {TURNSTILE_SITE_KEY && (
            <div className="signup-captcha">
              <Turnstile
                siteKey={TURNSTILE_SITE_KEY}
                onSuccess={(token) => setTurnstileToken(token)}
                onExpire={() => setTurnstileToken(null)}
                onError={() => {
                  setTurnstileToken(null);
                  setError('CAPTCHA failed to load. Please refresh and try again.');
                }}
              />
            </div>
          )}

          <button
            type="submit"
            className="btn btn-primary btn-signin"
            disabled={
              isSubmitting ||
              !form.fullName ||
              !form.email ||
              !form.companyName ||
              !form.interest ||
              (!!TURNSTILE_SITE_KEY && !turnstileToken)
            }
            aria-busy={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span className="btn-spinner" aria-hidden="true" />
                Sending…
              </>
            ) : (
              'Request access'
            )}
          </button>
        </form>

        {/* Footer toggle */}
        <div className="login-toggle">
          <p>
            Already have an account?{' '}
            <Link to="/" className="link-button">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
